import React, {ChangeEvent, useRef, useState, useEffect} from 'react'
import { useHistory, useLocation } from 'react-router';
import {exitReact, exitReactStyles} from "../../../utils";
import Api from '../../../api';
import * as ls from 'utils/localStorage';
import { useActionApi } from '../../../hooks';
import { roles } from "utils/constants";
import { trackLead, trackViewContent } from '../../../utils/facebookPixel';
import { 
    trackRoleSelected as gtmTrackRoleSelected,
    trackPageView as gtmTrackPageView 
} from '../../../utils/gtmDataLayer';

import {
    Page,
    Container
} from "./RoleSelect.styles";
import {Button, Checkbox, FormControlLabel} from "@material-ui/core";
import SignUpHeader from "../../../components/SignUpHeader";
import HeaderStatusBar from "../../../components/HeaderStatusBar";
import { UserAccountProps } from 'utils/types';
import FixedLoader from "../../../components/FixedLoader/FixedLoader";

// @ts-ignore
const TempStars = window.TempStars;

const inactiveIcon = "img/dotCheck_inactive.svg";
const activeIcon = "img/dotCheck_active.svg";
const checkIcon = "img/check-bold.svg";

enum Role {
    office = 'office',
    worker = 'worker',
}

const signupButtonLabelByRole = {
    [Role.office]: 'Signup as Dental Office',
    [Role.worker]: 'Signup as Dental Professional'
}

interface LocationState {
    userId?: number;
}

export default function RoleSelect() {
    const history = useHistory();
    const location = useLocation<LocationState>();
    const [userId, setUserId] = useState<number | undefined>(location.state?.userId);

    const [selectedRole, setSelectedRole] = useState<Role | undefined>();
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const checkWorkerRef = useRef<HTMLButtonElement>(null)
    const checkOfficeRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (!userId) {
            const userAccount = ls.get<UserAccountProps>('userAccount');
            if (userAccount && userAccount.id) {
                setUserId(userAccount.id);
                TempStars.Analytics.identify(String(userAccount.id));
            }
        } else {
            TempStars.Analytics.identify(String(userId));
        }
        
        // Track role selection page view
        // Facebook Pixel (existing)
        trackViewContent('Role Selection', 'Sign Up Flow');
        
        // GTM dataLayer (new)
        gtmTrackPageView(window.location.pathname, 'Role Selection - Sign Up Flow');
    }, [userId]);


    const changeRole = (event: ChangeEvent<HTMLInputElement>) => {
        event.preventDefault()
        setSelectedRole(event.target.name as Role)
    }

    const goNext = async () => {
        if (selectedRole) {
            setErrorMessage('');
            
            const userType = selectedRole === Role.office ? 'Dental Office' : 'Dental Worker';
            
            TempStars.Analytics.track('sign-up - Confirm user type', {userType});
            
            // Track role selection (mid-funnel conversion point)
            // Facebook Pixel (existing)
            trackLead('Role Selected', { 
                role: userType,
                roleId: selectedRole === 'office' ? roles.DENTIST : roles.HYGIENIST
            });
            
            // GTM dataLayer (new)
            const userTypeGTM = selectedRole === Role.office ? 'dental_office' : 'dental_professional';
            gtmTrackRoleSelected(userTypeGTM, {
                role: userType,
                roleId: selectedRole === 'office' ? roles.DENTIST : roles.HYGIENIST
            });
            
            setLoading(true);
            try {
                const roleId = selectedRole === 'office' ? roles.DENTIST : roles.HYGIENIST;
                const pathname = selectedRole === 'office' ? 'SignUp1_DO' : 'SignUp1_Talent';
                await Api.createUserRole({ roleId, userId: Number(userId) });
                history.push({
                    pathname,
                }, {
                    userId,
                })
            } catch (e) {
                setLoading(false);
                setErrorMessage('Something went wrong. Please try again.');
            }
        } else {
            setErrorMessage('Please choose an option');
        }
    }

    const onBack = () => {
        history.push({
            pathname: 'landing'
        });
    }

    const login = () => {
        history.push({
            pathname: 'Login',
        })
    }

    return (
        <Page>
            <Container data-page="role-select" className={'no-swipeback'}>
                <HeaderStatusBar onButton={onBack} buttonText={'Back'} withArrow />

                <div className={"bodyForm"}>
                    <SignUpHeader
                        title= {'What do you want to do?'}
                        error={errorMessage}
                    />

                    <div className="worker no-fastclick" id={Role.worker} onClick={() => checkWorkerRef.current?.click()}>
                        <FormControlLabel
                            className={'roleSelectLabel no-fastclick'}
                            control={<Checkbox
                                icon={<img id="workerCheckmark" className="hidden-icon" alt="" src={inactiveIcon} />}
                                checkedIcon={<img id="workerCheckmark" className="hidden-icon" alt="" src={activeIcon} />}
                                name={Role.worker}
                                checked={selectedRole === Role.worker}
                                ref={checkWorkerRef}
                                onChange={changeRole}
                            />}
                            label="I'm looking for work"
                        />

                        <div className="checklist">
                            <div className="itemBox">
                                <img className="hidden-icon" alt="" src={checkIcon} />
                                <div className="always-free">Choose your own hourly rate</div>
                            </div>
                            <div className="itemBox">
                                <img className="hidden-icon" alt="" src={checkIcon} />
                                <div className="always-free">Set your own schedule</div>
                            </div>
                            <div className="itemBox">
                                <img className="hidden-icon" alt="" src={checkIcon} />
                                <div className="always-free">Temping shifts + permanent/contract positions</div>
                            </div>
                            <div className="itemBox">
                                <img className="hidden-icon" alt="" src={checkIcon} />
                                <div className="always-free">100% FREE for dental professionals</div>
                            </div>
                        </div>
                    </div>

                    <div className="office no-fastclick" id={Role.office} onClick={() => checkOfficeRef.current?.click()}>
                        <FormControlLabel
                            className={'roleSelectLabel no-fastclick'}
                            control={<Checkbox
                                icon={<img id="officeCheckmark" className="hidden-icon" alt="" src={inactiveIcon} />}
                                checkedIcon={<img id="officeCheckmark" className="hidden-icon" alt="" src={activeIcon} />}
                                name={Role.office}
                                checked={selectedRole === Role.office}
                                ref={checkOfficeRef}
                                onChange={changeRole}
                            />}
                            label="My office needs temping or hiring"
                        />

                        <div className="checklist">
                            <div className="itemBox">
                                <img className="hidden-icon" alt="" src={checkIcon} />
                                <div className="always-free">170,000+ Temping Shifts Completed</div>
                            </div>
                            <div className="itemBox">
                                <img className="hidden-icon" alt="" src={checkIcon} />
                                <div className="always-free">85% Fulfillment Rate in most areas</div>
                            </div>
                            <div className="itemBox">
                                <img className="hidden-icon" alt="" src={checkIcon} />
                                <div className="always-free">
                                    97.1% Report "Happy" or "Very Happy" with their TempStars dental professional
                                </div>
                            </div>
                        </div>
                    </div>

                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        id="signup-button"
                        className="bigButtonInNewStyle"
                        onClick={goNext}
                        disabled={loading}
                    >
                        {selectedRole !== undefined ? signupButtonLabelByRole[selectedRole] : 'Signup'}
                    </Button>

                    <div className={'toLogIn'}>
                        {`Already have an account? `}
                        <div className="underlinedLink" onClick={login}>Log In</div>
                    </div>

                </div>

                <div className="footer footerLinks"></div>
            </Container>
            <FixedLoader loading={loading} />
        </Page>
    )
}
